import { v4 as uuidv4 } from 'uuid';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect } from 'react';
import { createSlice } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { cometChatLogin } from '../api/chatAuth';
import { LOGOUT_SUCCESSFUL } from '../reducers/auth';

const initialState = {
  authToken: null,

  // random uid used if deviceId not accessible
  // shared across all sessions per device
  fallbackDeviceId: null,

  // unread messages for indicator
  unreadMessagesCount: null,
};

const { name, reducer, actions } = createSlice({
  name: 'cometChat',
  initialState,
  reducers: {
    setUnreadMessagesCount(state, { payload }) {
      state.unreadMessagesCount = payload;
    },
    updateUnreadMessagesCount(state, { payload }) {
      state.unreadMessagesCount += payload;
      if (state.unreadMessagesCount < 0) {
        state.unreadMessagesCount = 0;
      }
    },
    setFallbackDeviceId(state, { payload }) {
      state.fallbackDeviceId = payload;
    },
    authorizationSuccessful(state, { payload }) {
      Object.assign(state, payload);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(LOGOUT_SUCCESSFUL, (state) => ({
      ...initialState,
      fallbackDeviceId: state.fallbackDeviceId,
    }));
  },
});

export const {
  setUnreadMessagesCount,
  updateUnreadMessagesCount,
  setFallbackDeviceId,
  authorizationSuccessful,
  extraReducers,
} = actions;

const cometChatSelector = ({ [name]: data }) => data;

export const unreadMessagesSelector = createSelector(
  cometChatSelector,
  ({ unreadMessagesCount }) => unreadMessagesCount,
);

export const useFallbackDeviceId = () => {
  const { fallbackDeviceId } = useSelector(cometChatSelector);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!fallbackDeviceId) {
      const deviceId = uuidv4();
      dispatch(setFallbackDeviceId(deviceId));
    }
  }, [fallbackDeviceId, dispatch]);

  return fallbackDeviceId;
};

export const authorizeChatUser =
  (apiUrl, passedDeviceId) => async (dispatch, getState) => {
    const {
      auth: { token },
      [name]: { fallbackDeviceId },
    } = getState();

    if (!apiUrl || !token) {
      return {};
    }

    let deviceId = passedDeviceId || fallbackDeviceId;

    if (!deviceId) {
      deviceId = uuidv4();
      dispatch(setFallbackDeviceId(deviceId));
    }

    const authData = await cometChatLogin(apiUrl, token, deviceId);
    dispatch(authorizationSuccessful(authData));

    return authData;
  };

export default { [name]: reducer };
